import config from 'isomorphic-config'
import analytics from '@/shared-utils/analytics'
import hasGraphErrors from '@/shared-utils/graph-utils'
import isAbortSignalSupported from '@/shared-utils/is-abortSignal-supported'
import type { GraphResponse, LOCATION_SEARCH_TYPE } from '@/types'

type CityInfo = Readonly<{
  cityID: number | null
  cityName: string | null
  stateCode: string | null
  stateName: string | null
  countryCode: string | null
  countryName: string | null
  itemName: string | null
  latitude: number | null
  longitude: number | null
  lat: number | null
  lon: number | null
}>

type CityInfoQueryGraphResponse = GraphResponse<{
  [key in `city_${number}`]: CityInfo | null
}>

type QueryAccumulator = {
  variableDefinitions: string[]
  cityInfoFields: string[]
  variables: Record<string, string>
}

function isValidGraphQLResponse(
  payload: unknown
): payload is CityInfoQueryGraphResponse {
  if (payload && typeof payload === 'object') {
    if (
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object' &&
      Object.values(payload.data).every(value => {
        return (
          value === null ||
          (value &&
            typeof value === 'object' &&
            'itemName' in value &&
            'cityID' in value)
        )
      })
    ) {
      return true
    }
  }
  return false
}

function transformCityResponseToLocationSearch(
  cityInfo: CityInfo
): LOCATION_SEARCH_TYPE {
  return {
    ...cityInfo,
    type: 'CITY',
    id: cityInfo?.cityID?.toString() ?? '',
    cityID: cityInfo?.cityID?.toString() ?? '',
    itemName: cityInfo?.itemName?.toString() ?? '',
    cityName: cityInfo?.cityName?.toString() ?? '',
    countryName: cityInfo?.countryName?.toString() ?? '',
    countryCode: cityInfo?.countryCode?.toString() ?? '',
    lat: cityInfo?.lat ?? undefined,
    lon: cityInfo?.lon ?? undefined
  }
}

function generateQuery(locations: ReadonlyArray<string>) {
  const uniqueLocations = Array.from(new Set(locations))
  const { variableDefinitions, cityInfoFields, variables } =
    uniqueLocations.reduce(
      (acc: QueryAccumulator, cityId, index) => {
        const variableName = `cityId${index}`
        acc.variableDefinitions.push(`$${variableName}: String!`)
        acc.cityInfoFields.push(`
        city_${cityId}: cityInfo(location: $${variableName}) {
          cityName
          countryCode
          countryName
          stateCode
          stateName
          latitude
          longitude
          itemName: displayCityName
          lat
          lon
          cityID: cityId
        }
      `)
        acc.variables[variableName] = cityId
        return acc
      },
      {
        variableDefinitions: [],
        cityInfoFields: [],
        variables: {}
      }
    )

  const query = `
    query fetchMultipleCityInfo(${variableDefinitions.join(', ')}) {
      ${cityInfoFields.join(' ')}
    }
  `

  return {
    query,
    variables
  }
}

export default async function fetchMultipleCityInfo(
  cityIds: ReadonlyArray<string>,
  appName: string,
  appVersion: string,
  cguid: string
) {
  const { query, variables } = generateQuery(cityIds)
  const OPERATION_NAME = 'fetchMultipleCityInfo'
  try {
    const { url, timeout } = config.client['pcln-graph']
    const options = {
      method: 'POST',
      signal: isAbortSignalSupported()
        ? AbortSignal.timeout(timeout)
        : undefined,
      headers: {
        'content-type': 'application/json',
        'apollographql-client-name': appName,
        'apollographql-client-version': appVersion
      },
      body: JSON.stringify({
        query,
        variables,
        operationName: OPERATION_NAME
      })
    }
    const requestUrl = `${url}?gqlOp=fetchMultipleCityInfo`
    const response = await fetch(requestUrl, options)
    const { status, url: urlValue } = response
    if (!response.ok) {
      analytics.logError({
        message: 'The cityInfo graph query response status is > 299',
        url: urlValue,
        status
      })
      return []
    }
    const payload = await response.json()
    if (isValidGraphQLResponse(payload)) {
      if (hasGraphErrors(payload)) {
        const firstErrorMessage = payload.errors?.[0]?.message
        const errorMessage = firstErrorMessage || 'Unknown error'
        const errorObjectAsString = JSON.stringify(payload.errors)
        const message = 'The cityInfo graph query response has returned errors.'
        analytics.logError({
          cguid,
          message,
          errorMessage,
          url: requestUrl,
          appName,
          appVersion,
          errorObjectAsString
        })
      } else {
        return Object.values(payload.data)
          .filter(cityInfo => cityInfo !== null)
          .map(cityInfo => transformCityResponseToLocationSearch(cityInfo))
      }
    } else {
      analytics.logError({
        cguid,
        message: `The cityInfo graph query response is not as per contract`,
        payload: JSON.stringify(payload)
      })
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : (error?.toString() ?? 'Unknown Error')
    analytics.logError({
      message: `Failed to call cityInfo query. Error message: ${errorMessage}`
    })
  }
  return []
}
